import React, { useEffect } from 'react';
import Cookies from 'js-cookie';
import { ExchangeForToken, LoginViaEmail } from 'api/authentication';
import { CircularProgress, Grid, Typography, useTheme } from '@mui/material';
import linkedInIcon from '../../assets/linkedin_icon.png'
import { Secondary } from 'ui-components/Buttons';
import { jwtDecode } from 'jwt-decode';
import { checkForDomain, setTokenForAuthorization } from 'utils/index';
import { useNavigate, useSearchParams } from 'react-router-dom';

const LINKEDIN_CLIENT_ID = process.env.REACT_APP_LINKEDIN_APP_ID;
const LINKEDIN_CLIENT_SECRET = process.env.REACT_APP_LINKEDIN_APP_SECRET;
const REDIRECT_URI = `${window.location.origin}/callback`;
const STATE = Math.random().toString(36).substring(2); // Secure random string

const LinkedinLogin = () => {
  const theme = useTheme();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams()
  const loginMethod = "linkedin";
  const { mutateAsync: exchangeForTokenAsync, status: exchangeForTokenStatus, error: exchangeForTokenError, isLoading: isLoadingExchangeForToken, data: exchangeForTokenResponse } = ExchangeForToken();
  const { mutateAsync: loginViaEmailAsync, status: LoginViaEmailStatus, error: LoginViaEmailError, isLoading, data: loginViaEmailResponse } = LoginViaEmail();



  useEffect(() => {
    const handleMessage = async (event) => {
      // console.log(event, "event")
      if (event.origin !== window.location.origin) return;
      if (event.data.provider === "linkedin") {
        const decodedData = jwtDecode(event.data?.details?.id_token);
        const currentIP = window.location.host;
        // // console.log('Login successful',decodedData);
        Cookies.set("userDetails", JSON.stringify({ first_name: decodedData?.given_name, last_name: decodedData?.family_name }))
        await loginViaEmailAsync({
          "email": decodedData?.email,
          ...(searchParams.get("utm_source") ? { utm_source: searchParams.get("utm_source") } : {}),
          ...(searchParams.get("utm_campaign") ? { utm_campaign: searchParams.get("utm_campaign") } : {}),
          "devices": {
            "device_token": `${Math.random() * 10}`,
            "device_type": "web"
          },
          "last_login_ip": `${currentIP}`,
          "auth_provider": "linkedin"
        }, {
          onSuccess: (verifyResponse) => {
            const response = verifyResponse?.data?.data;
            // console.log(verifyResponse, "ref")
            Cookies.set("email", decodedData?.email);
            Cookies.set("provider", "linkedin")
            if (currentIP?.includes("localhost")) {
              if (response?.collaborator_data?.connected_id) {
                Cookies.set("X-INV-ID", response?.collaborator_data?.connected_id);
              }
              response?.userData?.token && setTokenForAuthorization(response?.userData?.token);
              // response?.token && setUserType("investor");
            }
            // response?.token && setUserType("investor");
            // Session to be same for current users:
            response?.userData?.token && setTokenForAuthorization(response?.userData?.token, checkForDomain());
            Cookies.set("step", Number(response?.step));
            // console.log(response, "response")
            // Boolean(response?.permissions) && Cookies.set("permissions", JSON.stringify(response?.permissions));
            const pathname = Cookies.get("pathname") || "/";

            const hasCollaborator = Boolean(response?.collaborator_data?.connected_id);
            const hasInvestorId = Boolean(response?.investor_id);
            const claimedProfile = response?.claim_profile === true;
            const notClaimedAndNoInvestor = response?.claim_profile === false && !hasInvestorId;

            if (hasCollaborator) {
              Cookies.set("X-INV-ID", response?.collaborator_data?.connected_id, { domain: checkForDomain() });
              Boolean(response?.is_profile_completed) && Cookies.set("is_profile_complete", response?.is_profile_completed);
              navigate(pathname, { replace: true });
              Cookies.remove("pathname");
            } else if (claimedProfile && hasInvestorId) {
              Cookies.set("X-INV-TEMP-ID", response?.investor_id, { domain: checkForDomain() });
              Boolean(response?.is_profile_completed) && Cookies.set("is_profile_complete", response?.is_profile_completed);
              navigate("/activate-profile", { replace: true });
            } else if (notClaimedAndNoInvestor) {
              navigate("/onboarding", { replace: true });
            } else {
              Boolean(response?.is_profile_completed) && Cookies.set("is_profile_complete", response?.is_profile_completed);
              setTimeout(() => {
                navigate(response?.is_profile_completed ? pathname : "/onboarding", { replace: true });
                Cookies.remove("pathname");
              }, 10);
            }
          },
          onError: () => {
            // setEmailError(true);
          }
        });
        window.removeEventListener('message', handleMessage);
        // window.location.reload();
      }
    };
    window.addEventListener('message', handleMessage);
    return () => window.removeEventListener('message', handleMessage);
  }, []);


  React.useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const code = params.get('code');
    const state = params.get('state');
    const expectedState = sessionStorage.getItem('linkedin_oauth_state');
    // console.log(code,"code", state, expectedState)
    if (code ?? (code && window.location.pathname === "/callback")) {
      exchangeForTokenAsync({ code, redirect_uri: REDIRECT_URI, client_id: LINKEDIN_CLIENT_ID, client_secret: LINKEDIN_CLIENT_SECRET }).then((response) => {
        // console.log(response)
        if (window.opener) {
          window.opener.postMessage({
            provider: 'linkedin',
            details: response.data.details,
          }, window.location.origin);
        }
        window.close();
      }).catch(() => {
        if (window.opener) {
          window.opener.postMessage({ provider: 'linkedin', error: 'LinkedIn login failed' }, window.location.origin);
        }
        window.close();
      });
    }
  }, [])

  const handleLinkedinLogin = () => {
    // Save state in sessionStorage for CSRF protection
    sessionStorage.setItem('linkedin_oauth_state', STATE);
    const scope = 'email profile openid';
    const authUrl = `https://www.linkedin.com/oauth/v2/authorization?response_type=code&client_id=${LINKEDIN_CLIENT_ID}&redirect_uri=${encodeURIComponent(REDIRECT_URI)}&state=${STATE}&scope=${encodeURIComponent(scope)}`;
    const width = 600, height = 600;
    const left = (window.screen.width / 2) - (width / 2);
    const top = (window.screen.height / 2) - (height / 2);
    window.open(authUrl, 'LinkedIn Login', `width=${width},height=${height},top=${top},left=${left}`);

  };

  return (
    <Secondary
      onClick={handleLinkedinLogin}
      sx={{ border: `1px solid #E0E0E6`, borderRadius: '10px', height: '54px', marginBottom: { xs: "0rem", sm: 0 } }}
      fullWidth
    >
      {isLoading && loginMethod == "linkedin" ? <CircularProgress sx={{ color: "black", marginInline: 'auto' }} size={'1.4rem'} /> : <Grid
        display={"flex"}
        gap={1}
        flexDirection={"row"}
        alignItems={"center"}
      >
        <img src={linkedInIcon} width={24} height={24} />
        <Typography
          variant="caption"
          sx={{ textTransform: "none", fontWeight: 500, fontSize: '1rem', color: '#344054' }}
        >
          Continue with LinkedIn
        </Typography>
      </Grid>}
    </Secondary>
  );
};

export default LinkedinLogin;
